import { UserInfoDto } from '@growdash/identity/generated';
import { authorizeIdentityService } from '@lib/identity';
import axios from '@utils/axios';
import Cookies from 'js-cookie';
import { isTokenExpired } from './jwt';

// Constants
export const SESSION_KEY = 'gd_AccessToken';
export const REFRESH_TOKEN_KEY = 'gd_RefreshToken';
export const USER_KEY = 'gd_User';

// Helpers
// ----------------------------------------------------------------------
export const setCookieObject = ({
  key,
  value
}: {
  key: string;
  value: any;
}) => {
  Cookies.set(key, JSON.stringify(value), { expires: 365, path: '/' });
};

export const getCookieObject = ({ key }: { key: string }) => {
  return JSON.parse(Cookies.get(key) || '{}');
};

export const clearCookies = () => {
  const cookiesToClear = [SESSION_KEY, REFRESH_TOKEN_KEY, USER_KEY];

  cookiesToClear.forEach(cookieName => {
    Cookies.remove(cookieName, { path: '/' });
    Cookies.remove(cookieName, { path: '' });
    Cookies.remove(cookieName);
  });

  const domain = window.location.hostname;
  cookiesToClear.forEach(cookieName => {
    Cookies.remove(cookieName, { path: '/', domain });
    Cookies.remove(cookieName, { path: '/', domain: `.${domain}` });
  });
};

// ----------------------------------------------------------------------

export const getAccessToken: () => string | undefined = () => {
  return Cookies.get(SESSION_KEY);
};

export const getRefreshToken: () => string | undefined = () => {
  return Cookies.get(REFRESH_TOKEN_KEY);
};

export const getSessionUser: () => UserInfoDto = () => {
  return getCookieObject({ key: USER_KEY });
};

export const setSession = async (
  accessToken: string | null,
  refreshToken: string | null
) => {
  if (accessToken && !isTokenExpired(accessToken)) {
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    Cookies.set(SESSION_KEY, accessToken, { expires: 365, path: '/' });
    if (refreshToken) {
      Cookies.set(REFRESH_TOKEN_KEY, refreshToken, { expires: 365, path: '/' });
    }

    authorizeIdentityService(accessToken);
  } else {
    clearSession();
  }
};

export const setSessionRefreshToken = async (refreshToken: string | null) => {
  if (refreshToken) {
    Cookies.set(REFRESH_TOKEN_KEY, refreshToken, { expires: 365, path: '/' });
  }
};

// Clear both session, cookies and cache with comprehensive cleanup
export const clearSession = () => {
  try {
    clearCookies();

    sessionStorage.clear();

    const authKeys = [
      'persist:auth',
      'persist:root',
      SESSION_KEY,
      REFRESH_TOKEN_KEY,
      USER_KEY
    ];
    authKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore errors
      }
    });

    delete axios.defaults.headers.common.Authorization;

    authorizeIdentityService(null);

    // Force reload the axios instance to clear any cached headers
    if (axios.defaults.headers.common.Authorization) {
      axios.defaults.headers.common.Authorization = '';
    }

    // Clear any other potential auth-related cache
    if (typeof window !== 'undefined') {
      // Clear any potential cached credentials in the browser
      try {
        if (
          navigator.credentials &&
          navigator.credentials.preventSilentAccess
        ) {
          navigator.credentials.preventSilentAccess();
        }
      } catch (e) {
        // Ignore errors for unsupported browsers
      }
    }
  } catch (error) {
    console.error('Error during session cleanup:', error);
    // Even if there's an error, ensure basic cleanup
    try {
      clearCookies();
      sessionStorage.clear();
      delete axios.defaults.headers.common.Authorization;
    } catch (e) {
      // Final fallback
    }
  }
};
